/**
 * Date utility functions for the PWA.
 */

/** Format a Date as YYYY-MM-DD */
export function formatDateISO(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/** Parse YYYY-MM-DD string to a local Date (no timezone shift) */
export function parseDateISO(dateStr: string): Date {
  const [y, m, d] = dateStr.split('-').map(Number);
  return new Date(y, m - 1, d);
}

/** Get today as YYYY-MM-DD */
export function todayISO(): string {
  return formatDateISO(new Date());
}

/** Add days to a date string (YYYY-MM-DD) and return new string */
export function addDays(dateStr: string, days: number): string {
  const d = parseDateISO(dateStr);
  d.setDate(d.getDate() + days);
  return formatDateISO(d);
}

/** Add months to a date, clamping the day to the last day of the target month */
export function addMonths(date: Date, months: number): Date {
  const d = new Date(date);
  const targetMonth = d.getMonth() + months;
  d.setDate(1);
  d.setMonth(targetMonth);
  // Clamp: if original day exceeds target month's days, use last day
  const lastDay = new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate();
  d.setDate(Math.min(date.getDate(), lastDay));
  return d;
}

/** Get the first day of the month for a given date */
export function startOfMonth(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), 1);
}

/** Get the number of days in a month */
export function daysInMonth(date: Date): number {
  return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
}

/** Get the day of week (0=Sun, 6=Sat) for the first of the month */
export function firstDayOfWeek(date: Date): number {
  return new Date(date.getFullYear(), date.getMonth(), 1).getDay();
}

/** Format time string (HH:MM) for display */
export function formatTime(time: string | null): string {
  if (!time) return '';
  const [h, m] = time.split(':').map(Number);
  const ampm = h >= 12 ? 'PM' : 'AM';
  const hour = h % 12 || 12;
  return `${hour}:${String(m).padStart(2, '0')} ${ampm}`;
}

/** Format an ISO datetime string to a display time */
export function formatDateTime(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
}

/** Check if a date string is today */
export function isToday(dateStr: string): boolean {
  return dateStr === todayISO();
}

/** Get today's start and end ISO strings for API queries */
export function todayRange(): { start: string; end: string } {
  const d = new Date();
  const start = new Date(d.getFullYear(), d.getMonth(), d.getDate()).toISOString();
  const end = new Date(d.getFullYear(), d.getMonth(), d.getDate() + 1).toISOString();
  return { start, end };
}

/** Get a month's date range for API queries */
export function monthRange(year: number, month: number): { start: string; end: string } {
  const start = new Date(year, month, 1).toISOString();
  const end = new Date(year, month + 1, 1).toISOString();
  return { start, end };
}

/** Priority label */
export function priorityLabel(priority: number): string {
  switch (priority) {
    case 2: return 'High';
    case 1: return 'Medium';
    default: return 'Low';
  }
}

/** Priority color variable */
export function priorityColor(priority: number): string {
  switch (priority) {
    case 2: return 'var(--danger)';
    case 1: return 'var(--warning)';
    default: return 'var(--text-muted)';
  }
}
